import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CommunityLevelSection } from "@eolas-medical/core";
import {
  creatinineClearance,
  CreatinineClearanceArgs,
  ClinicalCalculatorsSubSection,
  CreatinineClearanceReturnValues,
} from "@eolas-medical/clinical-calculators";

import { useMainSectionVisit } from "Hooks";
import { InnerPageWrapper, Modal, NavButton, PageTitle, WarningModal } from "UIKit";
import { CalendarIcon, CreatinineClearanceIcon, HeightIcon, WeightIcon } from "Assets";

import { useCalculatorWarning } from "../hooks";
import { ClinicalCalculatorRouteProps } from "../types";
import { ResultsModal } from "../components/ResultsModal";
import { CalculatorForm } from "../components/CalculatorForm";
import { AttentionModal } from "../components";

interface CreatinineClearanceCalculatorState {
  values?: CreatinineClearanceArgs;
  result?: CreatinineClearanceReturnValues;
}

export const CreatinineClearanceCalculator: React.FC<ClinicalCalculatorRouteProps> = ({
  history,
  match: { params },
}) => {
  const { t } = useTranslation();
  const [state, setState] = useState<CreatinineClearanceCalculatorState>({});

  const { warning, setWarningMessage } = useCalculatorWarning(params.id);

  useMainSectionVisit({
    mainSectionId: CommunityLevelSection.clinicalCalculators,
    subSectionId: "creatinine clearance",
  });

  const handleSubmit = (values: CreatinineClearanceArgs) => {
    const result = creatinineClearance(values);
    setState({ result, values });
  };

  return (
    <InnerPageWrapper>
      <PageTitle title={t("clinicalCalculators_creatinine_title")} />
      <NavButton onClick={history.goBack} />

      <CalculatorForm
        onSubmit={handleSubmit}
        schemaName="creatinineClearance"
        values={[
          {
            id: "age",
            label: t("clinicalCalculators_age_label"),
            inputProps: {
              type: "number",
              iconLeft: <CalendarIcon className="w-4" />,
              placeholder: t("clinicalCalculators_age_placeholder"),
            },
          },
          {
            id: "weight",
            label: t("clinicalCalculators_weight_label"),
            inputProps: {
              type: "number",
              iconLeft: <WeightIcon className="w-4" />,
              placeholder: t("clinicalCalculators_weight_placeholder"),
            },
          },
          {
            id: "height",
            label: t("clinicalCalculators_height_label"),
            inputProps: {
              type: "number",
              iconLeft: <HeightIcon className="w-4" />,
              placeholder: t("clinicalCalculators_height_placeholder"),
            },
          },
          {
            id: "creatinine",
            label: t("clinicalCalculators_creatinine_label"),
            inputProps: {
              type: "number",
              iconLeft: <CreatinineClearanceIcon className="w-4" />,
              placeholder: t("clinicalCalculators_creatinine_placeholder"),
            },
          },
        ]}
      />
      <AttentionModal calculatorType={ClinicalCalculatorsSubSection.creatinineClearance} />
      <Modal open={!!(state.result && state.values)} onClose={() => setState({})}>
        {state.result && state.values && (
          <ResultsModal
            handleClose={() => setState({})}
            result={state.result}
            values={state.values}
            type={ClinicalCalculatorsSubSection.creatinineClearance}
          />
        )}
      </Modal>

      <WarningModal
        isOpen={!!warning}
        title="Warning Message"
        text={warning}
        confrimTitle="Got it"
        onConfirm={() => setWarningMessage("")}
      />
    </InnerPageWrapper>
  );
};
